import { Component, OnInit, inject } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { UploadService } from 'src/app/shared/services/upload/upload.service';
import { MySpaceService } from '../my-space.service';

@Component({
  selector: 'app-logo-editor',
  templateUrl: './logo-editor.component.html',
  styleUrls: ['./logo-editor.component.scss'],
  standalone: false,
})
export class LogoEditorComponent implements OnInit {
  sizes = ['48x48', '72x72', '96x96', '144x144', '168x168', '192x192', '512x512', 'mini', 'w70', 'w140', 'full-size'];
  imageLinks = Array(this.sizes.length);
  storage;
  showUploader = Array(this.sizes.length);
  isDefaultImage = Array(this.sizes.length);
  uploadedFiles = Array(this.sizes.length);
  wrongResolutionError = Array(this.sizes.length);
  loading = Array(this.sizes.length);
  constructor(
    private env: EnvService,
    private confirmService: AppConfirmService,
    private mySpaceService: MySpaceService,
    private authService: AuthService,
    private toast: ToastService,
    private translate: TranslateService,
    private uploadService: UploadService
  ) {
    this.storage = inject(Storage);
  }

  async getDownloadURL(url) {
    try {
      const reference = ref(this.storage, url);
      const result = await getDownloadURL(reference);
      return result;
    } catch (error) {
      console.error(error);
      return '';
    }
  }
  ngOnInit(): void {
    for (const [index, size] of this.sizes.entries()) {
      this.getImage(size, index);
    }
  }

  getImageResolution(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result;
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          reject();
        };
        if (typeof src === 'string') {
          img.src = src;
        }
      };
      reader.readAsDataURL(file);
    });
  }

  onFileSelected(event, size, index) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.uploadedFiles[index] = file;
      const params = this.getLogoParams(size, index);
      this.getImageResolution(file).then((imageRes) => {
        if (params.size) {
          const size = params.size;
          if (imageRes.width != size.x || imageRes.width != size.y) {
            this.wrongResolutionError[index] = true;
          } else {
            this.wrongResolutionError[index] = false;
          }
        }
      });
    }
  }

  uploadImage(size, index) {
    this.loading[index] = true;
    try {
      this.authService.getUserData().then(async (userData) => {
        const userID = (userData as any).id;
        const imageFileName = `${this.env.appName}_${size}.png`;
        const file = this.uploadedFiles[index];
        const imageRes = await this.getImageResolution(file);

        const params = this.getLogoParams(size, index);
        if (this.wrongResolutionError[index]) {
          return;
        }
        const filename = file.name;
        const extension = filename.substr(filename.lastIndexOf('.') + 1);

        const allowedExtensions = ['png', 'jpg'];
        if (allowedExtensions.includes(extension)) {
          this.uploadService.uploadFileAndGetProgressWitoutAppFolderPrefix(file, this.getFolderName() + '/' + imageFileName).on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            },
            (error) => {
              console.error(error);
              this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
              this.loading[index] = false;
            },
            () => {
              this.getImage(size, index);
              this.loading[index] = false;
            }
          );
        } else {
          this.toast.snackbar.open(this.translate.instant('Snackbar.UnsupportedExtension'), 'Ok', { duration: 2000 });
          this.loading[index] = false;
        }
      });
    } catch (err) {
      this.loading[index] = false;
    }
  }
  toggleUploader(index) {
    if (this.showUploader[index]) {
      this.showUploader[index] = !this.showUploader[index];
    } else {
      this.showUploader = Array(this.sizes.length);
      this.showUploader[index] = !this.showUploader[index];
    }
  }
  imageUploadedSucessfully(size, index) {
    this.getImage(size, index);
  }
  getLogoParams(size, index) {
    if (!size.includes('x')) {
      return { size: null, sizeStr: null, index };
    } else {
      return {
        size: {
          x: Number(size.split('x')[0]),
          y: Number(size.split('x')[1]),
        },
        sizeStr: size,
        index,
      };
    }
  }

  getFullFolderName() {
    const folderName = 'logos';
    return `${this.env.linkPrefix}ui.config/${folderName}`;
  }
  getFolderName() {
    const folderName = 'logos';
    return `ui.config/${folderName}`;
  }
  async getImage(suffix, index) {
    const folderName = 'logos';
    if (!folderName) {
      return;
    }
    try {
      this.env.linkPrefix;
      const currentImageURL = await this.getDownloadURL(`${this.env.linkPrefix}ui.config/${folderName}/${this.env.appName}_${suffix}.png`);
      if (currentImageURL.length) {
        this.imageLinks[index] = currentImageURL;
        this.isDefaultImage[index] = false;
      } else {
        this.imageLinks[index] = `assets/creatives/logo-${suffix}.png`;
        this.isDefaultImage[index] = true;
      }
    } catch (err) {
      this.imageLinks[index] = `assets/creatives/logo-${suffix}.png`;
      this.isDefaultImage[index] = true;
    }
  }

  async resetLogoImage(size, index) {
    const response = await firstValueFrom(this.confirmService.confirm());
    if (!response) {
      return;
    }
    this.mySpaceService.deleteCurrentLogo({ folder: this.getFolderName(), suffix: size }).subscribe({
      complete: () => {
        this.getImage(size, index);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
